import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';
import Switch from 'react-switch';
import { useMediaQuery } from 'react-responsive';

import CustomControl from '../../../../../components/global/CustomControl';
import Badge from '../../../../../components/global/Badge';
import { formatPriceForDisplay } from '../../../../../util/format-money';
import Button from '../../../../../components/global/Button';
import CheckmarkIcon from '../../../../../components/icons/CheckmarkIcon';
import Angle from '../../../../../components/global/Angle';
import EventAddonItemInformationModal from './EventAddonItemInformationModal';
import ShareIcon from '../../../../../components/icons/ShareIcon';
import WaldofiedFeatureComparisonModal from './WaldofiedFeatureComparisonModal';
import { breakpointDown } from '../../../../../services/responsive/responsive';

const propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  description: PropTypes.string,
  eventAddonItem: PropTypes.shape({
    price: PropTypes.shape({
      value: PropTypes.number,
    }),
  }),
  isSelected: PropTypes.bool,
  isPurchased: PropTypes.bool,
  onToggleSelect: PropTypes.func,
  features: PropTypes.arrayOf(PropTypes.string),
  howItWorks: PropTypes.object,
  basicSelected: PropTypes.bool,
  isWaldofiedAlbum: PropTypes.bool,
  className: PropTypes.string,
  featureStyle: PropTypes.oneOf(['single-column', 'double-column']),
};

const EventAddonItem = ({
  title,
  subtitle,
  description,
  eventAddonItem,
  disabled,
  isSelected,
  isPurchased,
  onToggleSelect,
  onTogglePlan,
  howItWorks,
  basicSelected,
  isWaldofiedAlbum,
  features,
  className,
  featureStyle = 'double-column',
}) => {
  const isMobile = useMediaQuery(breakpointDown('sm'));
  const [featuresModalOpen, setFeaturesModalOpen] = useState(false);
  const [howItWorksModalOpen, setHowItWorksModalOpen] = useState(false);
  const classes = classNames({
    [className]: !!className,
    'event-addon-item': true,
    disabled,
    selected: isSelected && !disabled,
  });

  const featureClasses = classNames({
    'event-addon-item-features feature-list': true,
    [featureStyle]: !!featureStyle,
  });

  const isFree = !eventAddonItem?.price?.value;

  return (
    <Button
      btnStyle="reset"
      className={classes}
      onClick={event => {
        event?.preventDefault?.();

        // This is certainly hacky, but as it stands, react-switch does not support
        // full control for events. Therefore, we cannot rely on stopping event propagation.
        // This shall allow us to at the very least manage things in such a way that we
        // properly capture the events we need to.
        if (
          event?.target?.classList &&
          event.target.classList.contains('react-switch-handle')
        ) {
          return;
        }

        if (disabled) {
          return;
        }

        onToggleSelect(eventAddonItem?.id);
      }}
    >
      <div
        className={classNames({
          'event-addon-item-header': true,
          'flex-column align-items-start align-items-md-center flex-md-row':
            !isPurchased && isWaldofiedAlbum,
        })}
      >
        <div className="d-flex justify-content-center align-items-center">
          {!isPurchased && (
            <CustomControl
              id="shutterbug-delivery-cb"
              type="checkbox"
              labelSrOnly
              checked={isSelected}
              readOnly
              disabled={disabled}
              size="lg"
              shape="squared"
            />
          )}
          <h4 className="mb-0">
            {title}
            {subtitle && (
              <span className="text-base text-italic text-muted ml-2">
                {subtitle}
              </span>
            )}
          </h4>
        </div>
        {isFree && !isPurchased ? (
          <Badge
            badgeSize="xl"
            className="btn-outline btn-outline-primary text-bold"
            badgeStyle="reset"
          >
            FREE
          </Badge>
        ) : (
          <Fragment>
            {isPurchased ? (
              <Badge
                badgeStyle="secondary"
                badgeSize="lg"
                className="text-bold"
              >
                Purchased
              </Badge>
            ) : (
              <Fragment>
                {isWaldofiedAlbum ? (
                  <div className="d-flex align-items-center mt-2 mt-md-0">
                    <Badge
                      className={classNames({
                        'mr-1 text-extra-bold': true,
                        'text-light': !basicSelected,
                      })}
                      badgeSize={isMobile ? 'sm' : 'lg'}
                      badgeStyle={basicSelected ? 'primary' : 'secondary'}
                    >
                      BASIC (FREE)
                    </Badge>
                    <Switch
                      id="waldofied-album-enabled"
                      uncheckedIcon={false}
                      checkedIcon={false}
                      height={24}
                      width={40}
                      onColor="#0d84fe"
                      offColor="#9bccfe"
                      onChange={(checked, event) => {
                        event?.stopPropagation?.();
                        onTogglePlan();
                      }}
                      checked={!basicSelected}
                      defaultChecked
                    />
                    <Badge
                      className={classNames({
                        'ml-1 text-extra-bold': true,
                        'text-light': basicSelected,
                      })}
                      badgeSize={isMobile ? 'sm' : 'lg'}
                      badgeStyle={!basicSelected ? 'primary' : 'secondary'}
                    >
                      Gold ($
                      {formatPriceForDisplay(eventAddonItem.price.value)})
                    </Badge>
                  </div>
                ) : (
                  <Badge badgeSize="lg" className="text-bold">
                    ${formatPriceForDisplay(eventAddonItem.price.value)}
                  </Badge>
                )}
              </Fragment>
            )}
          </Fragment>
        )}
      </div>
      {description && <p className="text-muted">{description}</p>}
      <ul className={featureClasses}>
        {features.map(feature => {
          return (
            <li key={feature?.id ?? feature}>
              <CheckmarkIcon
                color="#f1794e"
                size="14px"
                className="feature-icon"
              />
              <span>{feature?.label ?? feature}</span>
            </li>
          );
        })}
      </ul>
      {isWaldofiedAlbum && (
        <Fragment>
          <Button
            btnStyle="link"
            className="text-extra-bold"
            onClick={event => {
              event?.stopPropagation?.();
              setFeaturesModalOpen(true);
            }}
          >
            <ShareIcon size={12} color="#0d84fe" className="mr-1" /> Compare
            features
          </Button>
          <WaldofiedFeatureComparisonModal
            forceOpen={featuresModalOpen}
            onClose={event => {
              event?.stopPropagation?.();
              setFeaturesModalOpen(false);
            }}
            waldofiedEventAddonItem={eventAddonItem}
          />
        </Fragment>
      )}
      {howItWorks && (
        <Fragment>
          <Button
            btnStyle="link"
            className="text-extra-bold"
            onClick={event => {
              event?.stopPropagation?.();
              setHowItWorksModalOpen(true);
            }}
          >
            <Angle direction="right" className="mr-1" /> How it works
          </Button>
          <EventAddonItemInformationModal
            videoId={howItWorks.videoId}
            forceOpen={howItWorksModalOpen}
            title={`Learn more about ${pluralize(title, 0)}!`}
            onClose={event => {
              event?.stopPropagation?.();
              setHowItWorksModalOpen(false);
            }}
          />
        </Fragment>
      )}
    </Button>
  );
};

EventAddonItem.propTypes = propTypes;

export default EventAddonItem;
